import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How do I create an account?',
    answer:
      'Tap "Sign Up" and enter your email address and a password. You can also sign in with Apple for a faster setup. Once registered, you can start adding habits immediately.',
  },
  {
    question: 'How do I add a habit?',
    answer:
      'From the home screen, tap the "+" button to create a new habit. Enter a name, optional description, and choose a category. Your habit will appear on your daily dashboard ready for check-ins.',
  },
  {
    question: 'How do I check in to a habit?',
    answer:
      'On the home screen, tap the habit you want to mark as done for today. A checkmark will confirm your check-in, and your streak will update automatically.',
  },
  {
    question: 'How many habits can I track for free?',
    answer:
      'Free accounts can track up to 3 habits. Upgrade to Premium to add unlimited habits.',
  },
  {
    question: 'How do streaks work?',
    answer:
      'A streak counts the number of consecutive days you have completed a habit. Check in every day to keep your streak alive. Your current streak and best streak are displayed on each habit.',
  },
  {
    question: 'What happens if I miss a day?',
    answer:
      'If you miss a day without a streak freeze, your current streak resets to zero. Your best streak is always preserved so you can see your all-time record.',
  },
  {
    question: 'What is a streak freeze?',
    answer:
      'A streak freeze protects your streak for one day when you cannot check in. If you miss a day and have an active streak freeze, your streak will not reset. Streak freezes are a Premium feature.',
  },
  {
    question: 'What is the momentum score?',
    answer:
      'The momentum score reflects your overall consistency across all your habits. It takes into account your recent check-in frequency and active streaks to give you a single number that represents how well you are sticking to your routines.',
  },
  {
    question: 'What is included in Premium?',
    answer:
      'Premium includes unlimited habits (free plan allows up to 3), streak freezes to protect your streaks, advanced analytics with detailed charts and insights, and momentum score tracking across all habits.',
  },
  {
    question: 'How do I subscribe to Premium?',
    answer:
      'You can subscribe via the app. On iOS, subscriptions are handled through Apple In-App Purchase. On the web, payments are processed securely by Stripe. Navigate to Settings and tap "Upgrade to Premium" to see available plans.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer:
      'For iOS: go to your device Settings > Apple ID > Subscriptions > Streaks, and tap "Cancel Subscription". For web (Stripe): go to Settings in the app and tap "Manage Subscription" to access the Stripe customer portal. Your Premium features will remain active until the end of your current billing period.',
  },
  {
    question: 'How do I restore my purchases?',
    answer:
      'If you reinstall the app or sign in on a new device, go to Settings and tap "Restore Purchases". This will re-activate any existing subscription linked to your Apple ID or email address.',
  },
  {
    question: 'Can I export my data?',
    answer:
      'Yes. Go to Settings > Export Data to download a copy of your habits, check-ins, and streak history.',
  },
  {
    question: 'How do I delete my account?',
    answer:
      'Go to Settings > Account > Delete Account. This will permanently remove your account and all associated data. Alternatively, contact us at support@wolftechnologies.co.uk and we will process the deletion within 30 days.',
  },
  {
    question: 'Can I change my email address?',
    answer:
      'Currently, email changes require contacting support. Send us an email from your registered address with the new email you would like to use, and we will update it for you.',
  },
  {
    question: 'My data is not syncing across devices',
    answer:
      'Check that you are signed in with the same account on all devices. Ensure you have an active internet connection. Pull down on the home screen to force a refresh. If the issue persists, try signing out and back in.',
  },
  {
    question: 'I cannot log in',
    answer:
      'Double-check your email address and password. Use the "Forgot Password" option to reset your password via email. If you signed up with Apple, make sure you are using "Sign in with Apple" rather than email/password. If you are still unable to log in, contact us for assistance.',
  },
  {
    question: 'My streak did not update after checking in',
    answer:
      'Ensure your check-in was recorded (a checkmark should appear on the habit). Check that your device date and time are set correctly (Settings > General > Date & Time > Set Automatically). Pull down to refresh the home screen. If the problem continues, contact support with the habit name and the date in question.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const StreaksSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Streaks Support</h1>
          <p className="legal-page__subtitle">
            Get help with Streaks by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all support requests within 48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Streaks</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/streaks/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default StreaksSupport;
